import * as React from "react";
import { Box, Link, Typography } from "@mui/material";

export default function CircleEventsDoc({ onNavigate }) {
  return (
    <Box id="circle-events" sx={{ mb: 4 }}>
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        Circle — who is registered for an event?
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Circle powers community events (for example 501(c)(3) University workshops). Use this when
        you need to confirm whether someone is signed up, or who is on the roster for a session.
      </Typography>

      <Typography variant="h6" fontWeight="600" sx={{ mt: 2, mb: 1 }}>
        In Admin — two ways to review access
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph sx={{ mt: 0, mb: 1.5 }}>
        You can open <strong>Circle</strong> in the nav first (<strong>Circle Management</strong>)—it
        has shortcuts to <strong>Events</strong> and <strong>Users</strong>—or go straight to those
        lists from the sidebar.
      </Typography>
      <Box component="ul" sx={{ pl: 2, "& li": { mb: 1.25 } }}>
        <li>
          <strong>Start from the event (roster).</strong> Go to{" "}
          <strong>Circle → Events</strong>, open the event, then scroll to{" "}
          <strong>Attendees</strong>. You&apos;ll see who is registered, RSVP status, and can move
          through pages if the list is long. <strong>Click an attendee&apos;s email</strong> to open
          their Circle user profile (same place as <strong>Circle → Users</strong>). The{" "}
          <strong>code</strong> icon on a row expands raw technical details—only for troubleshooting.
        </li>
        <li>
          <strong>Start from the person (their events).</strong> Go to{" "}
          <strong>Circle → Users</strong>, open the member, then check{" "}
          <strong>Events Attending</strong>. That table lists what they&apos;re registered for; use{" "}
          <strong>View</strong> to open the event in Admin if you need the full event screen.
        </li>
      </Box>
      <Typography variant="body2" color="text.secondary" paragraph sx={{ mt: 1 }}>
        <strong>Don&apos;t mix this up with Dashboard → Users.</strong> The main{" "}
        <strong>Users</strong> area is for login accounts and which <em>organizations</em> they
        belong to. <strong>Circle → Users</strong> is for the Circle community member and their{" "}
        <em>event</em> registrations.
      </Typography>

      <Typography variant="h6" fontWeight="600" sx={{ mt: 3, mb: 1 }}>
        In the member portal (customers)
      </Typography>
      <Box component="ul" sx={{ pl: 2, "& li": { mb: 1 } }}>
        <li>
          <strong>Upcoming events</strong> — The <strong>home dashboard</strong> includes an upcoming
          events section so they can browse sessions and open details (including{" "}
          <strong>Learn more</strong> / enroll flows from there).
        </li>
        <li>
          <strong>My events</strong> — The <strong>My events</strong> page (in the app menu) lists
          workshops and sessions they are already enrolled in. From a row they can open{" "}
          <strong>Details</strong>.
        </li>
        <li>
          In the event details window they can <strong>Sign up</strong> to enroll, or{" "}
          <strong>Unenroll</strong> to remove their registration if they are already enrolled.
        </li>
        <li>
          If they are not on an eligible plan for in-app enrollment, the product may send them to
          the public 501(c)(3) University events page instead—have them enroll there, then you can
          confirm in Admin using the steps above.
        </li>
        <li>
          <strong>First-time enrollments:</strong> The system ties their login email to a Circle
          community member when they use enroll flows. If something looks missing, confirm they&apos;re
          signed in with the same email they used to register, then recheck Admin.
        </li>
      </Box>

      <Typography variant="h6" fontWeight="600" sx={{ mt: 3, mb: 1 }}>
        Communications (Circle vs our API)
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        <strong>Community and event communications</strong>—reminders, announcements, posts, and
        typical member emails about the Circle space—are handled by <strong>Circle</strong> (Circle&apos;s
        own notifications and email), not by our Stack API as a messaging system.
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        <strong>What our API does:</strong> It talks to Circle to <em>sync data</em>—events, RSVPs,
        and member records—so Admin and the portal show accurate enrollment. It does{" "}
        <strong>not</strong> replace Circle for day-to-day community communication. For questions
        like “why didn&apos;t they get the event reminder?”, troubleshooting usually starts in{" "}
        <strong>Circle</strong> (and the member&apos;s email/spam), not by looking for a “send email”
        button in our API.
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        <strong>Other product email</strong> (for example login, password reset, or billing from other
        parts of Brytebridge) may come from other systems—this section is only about{" "}
        <strong>Circle community / events</strong> messaging.
      </Typography>

      <Typography variant="h6" fontWeight="600" sx={{ mt: 3, mb: 1 }}>
        API (for engineering / integrations)
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        The member portal calls the Stack API (not Circle directly from the browser): listing events,
        the user&apos;s RSVPs, and subscribe/unsubscribe. Admin uses <strong>/admin/circle/...</strong>{" "}
        routes. Those endpoints move data to and from Circle—they are not a separate comms channel for
        members. Support staff don&apos;t need these paths day to day—use Admin and the portal as above.
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }} paragraph>
        <strong>Quick support line:</strong> “We can check the guest list under{" "}
        <strong>Circle → Events</strong> → your event → <strong>Attendees</strong>, or look up the
        person under <strong>Circle → Users</strong> → <strong>Events Attending</strong>. In the
        portal they manage this from the dashboard and <strong>My events</strong>.”
      </Typography>

      {onNavigate && (
        <Box sx={{ mt: 1, display: "flex", gap: 2, flexWrap: "wrap" }}>
          <Link
            component="button"
            variant="body2"
            onClick={() => onNavigate("/dashboard/circle")}
          >
            Circle (home) →
          </Link>
          <Link
            component="button"
            variant="body2"
            onClick={() => onNavigate("/dashboard/circle/events")}
          >
            Circle → Events →
          </Link>
          <Link
            component="button"
            variant="body2"
            onClick={() => onNavigate("/dashboard/circle/users")}
          >
            Circle → Users →
          </Link>
        </Box>
      )}
    </Box>
  );
}
